import { useRef, useState, useEffect } from 'react';
import { Video, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_DURATION = 30; // seconds

export default function VideoRecorder({ onRecorded }) {
  const [recording, setRecording] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [mime, setMime] = useState(null);
  const [duration, setDuration] = useState(0);
  const [streaming, setStreaming] = useState(false);
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const startTimeRef = useRef(0);

  useEffect(() => () => {
    streamRef.current?.getTracks?.().forEach(t => t.stop());
    clearInterval(timerRef.current);
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 }, audio: true });
      streamRef.current = stream;
      setStreaming(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const mr = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8,opus' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoUrl(url);
        setMime(mr.mimeType || 'video/webm');
        // base64
        const buf = await blob.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let bin = '';
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        const b64 = btoa(bin);
        onRecorded?.({ base64: b64, mime: mr.mimeType || 'video/webm' });
        stream.getTracks().forEach(t => t.stop());
        setStreaming(false);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        const d = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setDuration(d);
        if (d >= MAX_DURATION) stop();
      }, 300);
    } catch (e) {
      toast.error("Impossible d'accéder à la caméra. Autorise l'accès dans ton navigateur.");
      console.error(e);
    }
  };

  const stop = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const remove = () => {
    setVideoUrl(null);
    setDuration(0);
    onRecorded?.(null);
  };

  return (
    <div className="mt-3">
      {(streaming || videoUrl) && (
        <div className="rounded-xl overflow-hidden border border-stone-800 mb-2 max-w-sm">
          {streaming && !videoUrl ? (
            <video ref={videoRef} className="w-full h-auto" muted playsInline data-testid="video-preview-live" />
          ) : videoUrl ? (
            <video ref={previewRef} src={videoUrl} className="w-full h-auto" controls data-testid="recorded-video" />
          ) : null}
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        {!recording && !videoUrl && (
          <button data-testid="video-record-start" onClick={start} className="btn-ghost inline-flex items-center gap-2">
            <Video size={16} className="text-orange-400" /> Filmer 30s
          </button>
        )}
        {recording && (
          <button data-testid="video-record-stop" onClick={stop} className="btn-primary inline-flex items-center gap-2">
            <Square size={14} /> Stop · {duration}s / {MAX_DURATION}s
          </button>
        )}
        {videoUrl && !recording && (
          <button data-testid="video-record-delete" onClick={remove} className="text-stone-400 hover:text-red-400 inline-flex items-center gap-1 text-sm">
            <Trash2 size={14} /> Supprimer
          </button>
        )}
      </div>
    </div>
  );
}
